import { faCircle, faWarning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GreenPlateDTO } from 'api/model';
import { amber, gold, lightGreen } from 'constants/Colors';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { useTheme } from 'styled-components';

const mockNow = new Date();
export const mockGreenPlates: GreenPlateDTO[] = [
  // Never used (null date, should only appear in "Alla grönskyltar")
  {
    userID: 1,
    greenPlate: 'YZA567',
    isActiveUser: true,
    userFullName: 'Never Used',
    phoneNumber: '0700000009',
    lastUsedByUserAt: undefined,
  },
  // Used today (should only appear in "Alla grönskyltar")
  {
    userID: 2,
    greenPlate: 'ABC123',
    isActiveUser: true,
    userFullName: 'Active Recently',
    phoneNumber: '0700000001',
    lastUsedByUserAt: new Date(),
  },
  // Used 6 days ago (should appear in "Alla grönskyltar")
  {
    userID: 3,
    greenPlate: 'DEF456',
    isActiveUser: true,
    userFullName: 'Active 6 Days Ago',
    phoneNumber: '0700000002',
    lastUsedByUserAt: new Date(mockNow.getTime() - 6 * 24 * 60 * 60 * 1000),
  },
  // Used 7 days ago (should appear in 7days+ filters)
  {
    userID: 4,
    greenPlate: 'UVW567',
    isActiveUser: true,
    userFullName: 'Active 7 Days Ago',
    phoneNumber: '0700000009',
    lastUsedByUserAt: new Date(mockNow.getTime() - 7 * 24 * 60 * 60 * 1000),
  },
  // Used 10 days ago (should appear in 7days+ filters)
  {
    userID: 5,
    greenPlate: 'GHI789',
    isActiveUser: true,
    userFullName: 'Active 10 Days Ago',
    phoneNumber: '0700000003',
    lastUsedByUserAt: new Date(mockNow.getTime() - 10 * 24 * 60 * 60 * 1000),
  },
  // Used 14 days ago (should appear in 14days+ filters)
  {
    userID: 6,
    greenPlate: 'LMN890',
    isActiveUser: true,
    userFullName: 'Active 14 Days Ago',
    phoneNumber: '0700000011',
    lastUsedByUserAt: new Date(mockNow.getTime() - 14 * 24 * 60 * 60 * 1000),
  },
  // Used 20 days ago (should appear in 14days+ filters)
  {
    userID: 7,
    greenPlate: 'JKL012',
    isActiveUser: true,
    userFullName: 'Active 20 Days Ago',
    phoneNumber: '0700000004',
    lastUsedByUserAt: new Date(mockNow.getTime() - 20 * 24 * 60 * 60 * 1000),
  },
  // Used 30 days ago (should appear in 30days+ filters)
  {
    userID: 8,
    greenPlate: 'OPQ234',
    isActiveUser: true,
    userFullName: 'Active 30 Days Ago',
    phoneNumber: '0700000012',
    lastUsedByUserAt: new Date(mockNow.getTime() - 30 * 24 * 60 * 60 * 1000),
  },
  // Used 60 days ago (should appear in 30days+ filters)
  {
    userID: 9,
    greenPlate: 'MNO345',
    isActiveUser: true,
    userFullName: 'Active 60 Days Ago',
    phoneNumber: '0700000005',
    lastUsedByUserAt: new Date(mockNow.getTime() - 60 * 24 * 60 * 60 * 1000),
  },
  // Used 90 days ago (should appear in 90days+ filters)
  {
    userID: 10,
    greenPlate: 'XYZ456',
    isActiveUser: true,
    userFullName: 'Active 91 Days Ago', // compensate for daylight saving time
    phoneNumber: '0700000010',
    lastUsedByUserAt: new Date(mockNow.getTime() - 91 * 24 * 60 * 60 * 1000),
  },
  // Used 150 days ago (should appear in 90days+ filters)
  {
    userID: 11,
    greenPlate: 'PQR678',
    isActiveUser: true,
    userFullName: 'Active 150 Days Ago',
    phoneNumber: '0700000006',
    lastUsedByUserAt: new Date(mockNow.getTime() - 150 * 24 * 60 * 60 * 1000),
  },
  // Used 180 days ago (should appear in 180days+ filters)
  {
    userID: 12,
    greenPlate: 'RST890',
    isActiveUser: true,
    userFullName: 'Active 181 Days Ago', // compensate for daylight saving time
    phoneNumber: '0700000013',
    lastUsedByUserAt: new Date(mockNow.getTime() - 181 * 24 * 60 * 60 * 1000),
  },
  // Used 300 days ago (should appear in 180days+ filters)
  {
    userID: 13,
    greenPlate: 'STU901',
    isActiveUser: true,
    userFullName: 'Active 300 Days Ago',
    phoneNumber: '0700000007',
    lastUsedByUserAt: new Date(mockNow.getTime() - 300 * 24 * 60 * 60 * 1000),
  },
  // Used 365 days ago (should appear in 365days+ filters)
  {
    userID: 14,
    greenPlate: 'ABC678',
    isActiveUser: true,
    userFullName: 'Active 365 Days Ago',
    phoneNumber: '0700000014',
    lastUsedByUserAt: new Date(mockNow.getTime() - 365 * 24 * 60 * 60 * 1000),
  },
  // Used 400 days ago (should only appear in 365days+ filters)
  {
    userID: 15,
    greenPlate: 'VWX234',
    isActiveUser: true,
    userFullName: 'Active 400 Days Ago',
    phoneNumber: '0700000008',
    lastUsedByUserAt: new Date(mockNow.getTime() - 400 * 24 * 60 * 60 * 1000),
  },
];

const useGreenPlates = () => {
  const [selectedDateFilter, setSelectedDateFilter] = useState<string>('');
  const theme = useTheme();

  const filterOptions = useMemo(() => {
    return [
      { value: '1week', label: '1 vecka' },
      { value: '2weeks', label: '2 veckor' },
      { value: '1month', label: '1 månad' },
      { value: '3months', label: '3 månader' },
      { value: '6months', label: '6 månader' },
      { value: '1year', label: '1 år' },
    ];
  }, []);

  const getFilteredByDateOptions = (greenPlates: GreenPlateDTO[]) => {
    if (!selectedDateFilter.length) {
      return greenPlates;
    }
    const now = moment();
    let filterDate: moment.Moment;

    switch (selectedDateFilter) {
      case '1week':
        filterDate = now.clone().subtract(7, 'days');
        break;
      case '2weeks':
        filterDate = now.clone().subtract(14, 'days');
        break;
      case '1month':
        filterDate = now.clone().subtract(1, 'months');
        break;
      case '3months':
        filterDate = now.clone().subtract(3, 'months');
        break;
      case '6months':
        filterDate = now.clone().subtract(6, 'months');
        break;
      case '1year':
        filterDate = now.clone().subtract(1, 'years');
        break;
      default:
        return greenPlates;
    }

    return greenPlates.filter(
      (greenPlate) =>
        !!greenPlate.lastUsedByUserAt &&
        moment(greenPlate.lastUsedByUserAt).isBefore(filterDate)
    );
  };

  const getGreenPlateStatusIcon = (lastUsedByUserAt: Date) => {
    const lastUsed = moment(lastUsedByUserAt);
    const now = moment();
    const diffInWeeks = now.diff(lastUsed, 'weeks');
    const diffInMonths = now.diff(lastUsed, 'months');
    const diffInYears = now.diff(lastUsed, 'years');

    if (diffInYears >= 1) {
      return (
        <FontAwesomeIcon
          icon={faWarning}
          color={theme.colors.foreground.error}
          title="Mer än ett år sen"
        />
      );
    }

    if (diffInMonths >= 6) {
      return (
        <FontAwesomeIcon
          icon={faWarning}
          color={theme.colors.foreground.tint}
          title="Mer än ett halvår sen"
        />
      );
    }

    if (diffInMonths >= 3) {
      return (
        <FontAwesomeIcon
          icon={faCircle}
          color={theme.colors.foreground.tint}
          title="Mer än tre månader sen"
        />
      );
    }

    if (diffInMonths >= 1) {
      return (
        <FontAwesomeIcon
          icon={faCircle}
          color={amber}
          title="Mer än en månad sen"
        />
      );
    }

    if (diffInWeeks >= 2) {
      return (
        <FontAwesomeIcon
          icon={faCircle}
          color={gold}
          title="Mer än två veckor sen"
        />
      );
    }
    if (diffInWeeks >= 1) {
      return (
        <FontAwesomeIcon
          icon={faCircle}
          color={lightGreen}
          title="Mer än en vecka sen"
        />
      );
    }

    return (
      <FontAwesomeIcon
        icon={faCircle}
        color={theme.colors.foreground.good}
        title="Inom senaste veckan"
      />
    );
  };

  return {
    selectedDateFilter,
    setSelectedDateFilter,
    dateFilterOptions: filterOptions,
    getFilteredByDateOptions,
    getGreenPlateStatusIcon,
  };
};

export default useGreenPlates;
